import { useEffect, useState, useRef } from "react";
import { Modal, Button, Row, Col } from "react-bootstrap";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import { formatDate } from "../../utils/Utils";

const RichTextEditorModal = ({
  showModal,
  handleCloseModal,
  handleEditorChange,
  editorContent,
  handleSavePublish,
  coachingPresentVariables,
  isCoachingEditable,
  formData,
  profileData,
  privacyPolicyData,
  countriesArr,
  statesArr,
}) => {
  const { trans } = useCustomTranslation();
  const quillRef = useRef(null);

  const [activeVariables, setActiveVariables] = useState({
    businessName: false,
    streetAddress: false,
    zipCode: false,
    city: false,
    country: false,
    publishDate: false,
    coachEmail: false,
    dataPolicyLink: false,
    state: false,
    // vatId: false,
    // businessRegistrationId: false,
  });

  const [currentContent, setCurrentContent] = useState(editorContent || "");
  const [livePreviewHtml, setLivePreviewHtml] = useState("");
  const [variableCounts, setVariableCounts] = useState({});

  // Function to get country name by ID
  const getCountryName = (countryId) => {
    const country = countriesArr?.find(
      (c) => c.countryId === Number(countryId)
    );
    return country ? country.label : "";
  };
  // Function to get country name by ID
  const getStateName = (stateId) => {
    const state = statesArr?.find((c) => c.stateId === Number(stateId));
    return state ? state.label : "";
  };

  const variableValueMap = {
    businessName: formData?.businessLegalName || "",
    streetAddress: formData?.businessAddress || "",
    zipCode: formData?.zipCode || "",
    city: formData?.city || "",
    country: formData?.country ? getCountryName(formData?.country) : "",
    state: formData?.state ? getStateName(formData?.state) : "",
    coachEmail: profileData?.email || "",
    publishDate: formData?.coaching?.lastPublished
      ? formatDate(formData?.coaching?.lastPublished)
      : "-",
    dataPolicyLink: privacyPolicyData?.pdfURL
      ? `<a href="${privacyPolicyData.pdfURL}" target="_blank">Privacy Policy</a>`
      : "",
    // vatId: formData?.vatId || "",
    // businessRegistrationId: formData?.businessRegistrationId || "",
  };

  // Count variable occurrences in content
  const countVariables = (content) => {
    const counts = {};
    Object.keys(variableValueMap).forEach((variable) => {
      const regex = new RegExp(`\\{\\{\\s*${variable}\\s*\\}\\}`, "g");
      counts[variable] = (content.match(regex) || []).length;
    });
    return counts;
  };

  // Interpolates {{placeholders}} with values from the map
  const interpolateTemplate = (template) => {
    return template.replace(/\{\{(.*?)\}\}/g, (_, key) => {
      const trimmedKey = key.trim();
      return variableValueMap[trimmedKey] || `{{${trimmedKey}}}`;
    });
  };

  useEffect(() => {
    if (showModal) {
      setCurrentContent(editorContent || "");
      const preview = interpolateTemplate(editorContent || "");
      setLivePreviewHtml(preview);
      setVariableCounts(countVariables(editorContent || ""));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal, editorContent]);

  // Update preview when content changes
  useEffect(() => {
    const preview = interpolateTemplate(currentContent);
    setLivePreviewHtml(preview);
    setVariableCounts(countVariables(currentContent));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentContent]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    quill.keyboard.addBinding({ key: "Backspace" }, function (range) {
      const index = range.index;
      const length = quill.getLength();

      // Check the previous few characters
      const contextLength = 30;
      const start = Math.max(0, index - contextLength);
      const end = Math.min(length, index + 10); // small range after cursor
      const text = quill.getText(start, end - start);

      // Try to find the placeholder with optional spaces
      const match = [...text.matchAll(/\s?\{\{\s*\w+\s*\}\}\s?/g)].pop();

      if (match) {
        const matchText = match[0];
        const matchIndexInContext = match.index;
        const absoluteStart = start + matchIndexInContext;

        // Check if cursor is inside or just after the match
        if (
          index >= absoluteStart &&
          index <= absoluteStart + matchText.length
        ) {
          quill.deleteText(absoluteStart, matchText.length);
          return false; // Block default backspace
        }
      }

      return true;
    });
  }, []);

  // Format display name for variables
  const formatVariableName = (variable) =>
    variable
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

  const handleVariableClick = (variable) => {
    if (!isCoachingEditable) return;

    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    // non-breaking space
    // const placeholder = `\u00A0{{${variable}}}\u00A0`;
    const placeholder = ` {{${variable}}}`; // simple space — safer for Quill regex

    // const placeholder = `\u00A0{{${variable}}}`; // non-breaking space
    const range = editor.getSelection();
    const position = range?.index ?? editor.getLength();

    editor.insertText(position, placeholder);
    editor.setSelection(position + placeholder.length);

    // Update variable counts
    setVariableCounts((prev) => ({
      ...prev,
      [variable]: (prev[variable] || 0) + 1,
    }));
  };

  const handleModalClose = () => {
    setActiveVariables(coachingPresentVariables || {});
    handleCloseModal();
  };

  const handleSave = () => {
    const editor = quillRef.current?.getEditor();
    if (editor) {
      const content = editor.root.innerHTML;
      const counts = countVariables(content);
      const usedVariables = Object.fromEntries(
        Object.keys(counts).map((key) => [key, counts[key] > 0])
      );

      handleSavePublish(usedVariables);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={handleModalClose}
      className="custom-modal"
      size="xl" // Increased size for better layout
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {(isCoachingEditable ? trans("Common.edit") : trans("Common.view")) +
            " " +
            trans("Common.coaching") +
            " " +
            trans("Common.information")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row>
          {/* Editor Column */}
          <Col md={6}>
            <ReactQuill
              ref={quillRef}
              value={currentContent}
              onChange={(content) => {
                setCurrentContent(content);
                handleEditorChange(content);
              }}
              theme="snow"
              placeholder={trans("Toolbox.editorContentPlaceholder")}
              modules={{
                toolbar: [
                  [{ size: ["small", false, "large", "huge"] }],
                  ["bold", "italic", "underline"],
                  ["link"],
                  [{ list: "ordered" }, { list: "bullet" }],
                  [{ color: [] }, { background: [] }],
                ],
              }}
              style={{ height: "80%" }}
              readOnly={!isCoachingEditable}
            />
          </Col>

          {/* Preview Column */}
          <Col md={6}>
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="p-0 m-0 mb-1">{"Live Preview"}</h5>
              {/* <small className="text-muted">
                {Object.values(variableCounts).reduce((a, b) => a + b, 0)}{" "}
                variables used
              </small> */}
            </div>
            <div
              className="border p-3"
              style={{
                height: "90%",
                backgroundColor: "#f8f9fa",
                overflowY: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: livePreviewHtml }}
            />
          </Col>
        </Row>

        <div className="mt-4 px-3">
          <p>{trans("Toolbox.legalDisclaimer")}</p>
          <div className="d-flex justify-content-between align-items-center">
            <h5>{trans("Toolbox.presentvariables")}</h5>
            {/* <small className="text-muted">
              Click to insert variables multiple times
            </small> */}
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            {Object.keys(activeVariables).map((variable) => (
              <Button
                key={variable}
                variant="outline-light"
                size="sm"
                onClick={() => handleVariableClick(variable)}
                className={`present-variable-button ${
                  variableCounts[variable] > 0 ? "active" : "muted"
                }`}
                disabled={
                  !isCoachingEditable ||
                  (variable === "dataPolicyLink" &&
                    !variableValueMap.dataPolicyLink)
                }
                title={
                  variable === "dataPolicyLink" &&
                  !variableValueMap.dataPolicyLink
                    ? "Privacy policy URL not available"
                    : undefined
                }
              >
                {formatVariableName(variable)}
                {variableCounts[variable] > 0 && (
                  <span className="ms-1 badge">{variableCounts[variable]}</span>
                )}
              </Button>
            ))}
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button
          variant="outline-primary"
          onClick={handleModalClose}
          size="sm"
          className="cancel-button w-25 p-2"
        >
          {trans("Common.cancel")}
        </Button>
        {isCoachingEditable && (
          <Button
            className="saveButton w-25 ml-2"
            onClick={handleSave}
            variant="primary"
          >
            {trans("Common.savePublish")}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default RichTextEditorModal;
